import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from 'lodash';
import config from '../../config';
import * as CubeApi from '../../common/CubeApi';
import * as cache from "../../common/Cache";

import { parseCubeFilter, parseOrgConfigFilter, parseDynamicCubeFilter } from './CubeFilterBuilder';
import {buildGeoJson, buildPointDetailGeoJson} from "./GeoJsonBuilder";
import {login} from "../../common/account/accountSlice";

const COMMON_DIMENSIONS = [
    'company_id', 'company_identity', 'company_score', 'company_name',
    'company_url', 'company_url_status', 'company_address_delivery_line_1',
    'company_address_city', 'company_address_region', 'company_address_postal_code',
    'company_phone', 'company_email', 'company_email_status',
    'company_class_primary_trade', 'company_class_primary_trade_score', 'company_class_trades',
    'company_class_naics_titles_formatted',
    'company_profile_cnt', 'company_profile_types_mapped', 'company_profile_reviews_ratings_max',
    'company_profile_manufacturer_profile_cnt', 'company_profile_association_profile_cnt',
    'company_license_cnt', 'company_permit_cnt', 'company_policy_cnt',
    'company_registration_local_registration_status_mapped', 'company_contact_cnt', 'company_source_cnt',
    'contact_id', 'contact_identity', 'contact_score', 'contact_name_given_name',
    'contact_name_surname', 'contact_title', 'contact_phone', 'contact_email',
    'contact_email_status', 'contact_roles',
];

const createUniverseConfig = (cubeNamespace, orgConfigName, supportsMap, includeBaseCount = false) => ({
    cubeNamespace,
    orgConfigName,
    defaultOrder: {
        [`${cubeNamespace}.company_score`]: 'desc',
        [`${cubeNamespace}.company_class_primary_trade_score`]: 'desc'
    },
    listDimensions: COMMON_DIMENSIONS.map(dim => `${cubeNamespace}.${dim}`),
    countMeasures: includeBaseCount
        ? [`${cubeNamespace}.count`, `${cubeNamespace}.count_companies`, `${cubeNamespace}.count_contacts`]
        : [`${cubeNamespace}.count_companies`, `${cubeNamespace}.count_contacts`],
    mapLatField: supportsMap ? 'company_address_loc_city_latitude' : null,
    mapLonField: supportsMap ? 'company_address_loc_city_longitude' : null,
    supportsMap,
});

const UNIVERSE_CONFIG = {
    'BUSINESS': {
        'COMPANY': createUniverseConfig('CompanyContractorUniverse', 'Company', true, false),
        'CONTACT': createUniverseConfig('ContactContractorUniverse', 'Contact', false, false),
    },
    'CONTRACTOR': {
        // orgConfigName must match explore mode: 'Company' for company view so locked score
        // filters use the same cube namespace as dimensions/measures (not ContactContractorUniverse).
        'COMPANY': createUniverseConfig('TBDContractorUniverseCompanyExportCubeJS', 'Company', true, true),
        'CONTACT': createUniverseConfig('TBDContractorUniverseContactExportCubeJS', 'Contact', false, true),
    }
};

// initialState 
const initialState = {
    searchLoading: false,
    countLoading: false,
    error: null,
    results: {
        BUSINESS: {
            COMPANY: {
                listData: [],
                mapData: { features: [] },
                totalCompanies: 0,
                totalContacts: 0,
                appliedFilters: null,
            },
            CONTACT: {
                listData: [],
                mapData: { features: [] },
                totalCompanies: 0,
                totalContacts: 0,
                appliedFilters: null,
            },
            mapStatus: 'idle',
            mapErrorMessage: null,
            lastUpdated: null,
            productDataType: 'RECORD',
        },
        CONTRACTOR: {
            COMPANY: {
                listData: [],
                mapData: { features: [] },
                totalCompanies: 0,
                totalContacts: 0,
                appliedFilters: null,
            },
            CONTACT: {
                listData: [],
                mapData: { features: [] },
                totalCompanies: 0,
                totalContacts: 0,
                appliedFilters: null,
            },
            mapStatus: 'idle',
            mapErrorMessage: null,
            lastUpdated: null,
            productDataType: 'RECORD',
        }
    }
};

// The active accuracy grade (universes[key].accuracyData, set by the SCORE
// dropdown) overrides the static subscription score floor. Falls back to the
// restriction score when no grade is selected. Keep in sync with the SCORE pill
// in filters/AppliedFilters.jsx so the displayed score matches what's queried.
export const resolveEffectiveScore = (accuracyData, restrictions) => {
    if (accuracyData && accuracyData.minScore !== undefined && accuracyData.minScore !== null) {
        return accuracyData.minScore;
    }
    return restrictions?.scores;
};

const buildRestrictionFilters = (restrictions, namespace, scoreOverride) => {
    const filters = [];
    if (!restrictions) return filters;

    const score = scoreOverride !== undefined && scoreOverride !== null
        ? scoreOverride
        : restrictions.scores;
    if (score !== undefined && score !== null) {
        filters.push({
            and: [
                { member: `${namespace}.company_score`, operator: 'gte', values: [String(score)] },
                { member: `${namespace}.company_score`, operator: 'lte', values: ['1'] }
            ]
        });
    }
    if (Array.isArray(restrictions.tradeClassifications) && restrictions.tradeClassifications.length > 0) {
        filters.push({ member: `${namespace}.company_class_primary_trade`, operator: 'equals', values: restrictions.tradeClassifications.map(String) });
    }
    if (Array.isArray(restrictions.metropolitanStatisticalAreas) && restrictions.metropolitanStatisticalAreas.length > 0) {
        filters.push({ member: `${namespace}.company_address_loc_msa`, operator: 'equals', values: restrictions.metropolitanStatisticalAreas.map(String) });
    }
    if (Array.isArray(restrictions.regions) && restrictions.regions.length > 0) {
        filters.push({ member: `${namespace}.company_address_region`, operator: 'equals', values: restrictions.regions.map(String) });
    }
    return filters;
};

const buildSearchCriteriaFilter = (criteria, namespace) => {
    if (!criteria || criteria.trim() === '') return [];
    return [{
        or: [
            { member: `${namespace}.company_name`, operator: "contains", values: [String(criteria)] },
            { member: `${namespace}.company_id`, operator: "equals", values: [String(criteria)] },
            { member: `${namespace}.company_identity`, operator: "equals", values: [String(criteria)] }
        ]
    }];
};

export const resolveUniverseConfig = (universeKey, exploreByContact = false) => {
    const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';
    return UNIVERSE_CONFIG[universeKey]?.[modeKey];
};

// Reverse-lookup of UNIVERSE_CONFIG: given the cube namespace a saved search was
// built against (e.g. TBDContractorUniverseCompanyExportCubeJS), return which
// universe + mode it belongs to. A saved search's `type` only tells us
// company-vs-contact; both the BUSINESS and CONTRACTOR universes emit
// SEARCH_COMPANY/SEARCH_CONTACT, so the universe itself can only be recovered
// from the namespace. Returns null when the namespace isn't recognized.
export const resolveUniverseFromCubeNamespace = (cubeNamespace) => {
    if (!cubeNamespace) return null;
    for (const [universeKey, modes] of Object.entries(UNIVERSE_CONFIG)) {
        for (const [modeKey, cfg] of Object.entries(modes)) {
            if (cfg.cubeNamespace === cubeNamespace) {
                return { universeKey, exploreByContact: modeKey === 'CONTACT' };
            }
        }
    }
    return null;
};

const prepareFilters = (state, universeKey, viewMode = 'list') => {
    const filterEntities = state.universe.universes[universeKey]?.entities || {};
    const rawFilters = Object.values(filterEntities);

    const account = cache.get(config.constants.CACHE_KEY_ACCOUNT) || state.account?.account;
    const restrictions = state.universe.universes[universeKey]?.subscription?.restrictions
        || account?.productConfig?.restrictions;

    const searchQuery = state.universe.universes[universeKey]?.searchQuery || '';

    const exploreByContact = state.universe.universes[universeKey]?.exploreByContact ?? false;
    const uConfig = resolveUniverseConfig(universeKey, exploreByContact);
    const accuracyData = state.universe.universes[universeKey]?.accuracyData;
    const effectiveScore = resolveEffectiveScore(accuracyData, restrictions);
    const accuracyScore = null;

    const dynamicFiltersInput = rawFilters.filter(f => f.schemaProperty);
    const legacyFiltersInput = rawFilters.filter(f => !f.schemaProperty);

    const dynamicCubeFilters = parseDynamicCubeFilter(dynamicFiltersInput, uConfig.cubeNamespace);

    // Score-restriction precedence: subscription.restrictions.scores wins over
    // the legacy organization.config.scores. When the subscription provides a
    // score, strip it from the org config before passing it to
    // parseOrgConfigFilter so the same predicate isn't emitted twice.
    const subscriptionScoreSet = restrictions?.scores !== undefined && restrictions?.scores !== null;
    const orgConfigForFilters = subscriptionScoreSet && account?.organization?.config?.scores
        ? { ...account.organization.config, scores: undefined }
        : account?.organization?.config;

    const configFilters = parseOrgConfigFilter(
        legacyFiltersInput,
        orgConfigForFilters,
        uConfig.orgConfigName,
        accuracyScore,
        uConfig.cubeNamespace
    );
    const cubeUserFilters = configFilters?.length > 0
        ? parseCubeFilter(configFilters, uConfig.orgConfigName)
        : [];

    const restrictionFilters = buildRestrictionFilters(restrictions, uConfig.cubeNamespace, effectiveScore);
    const textSearchFilters = buildSearchCriteriaFilter(searchQuery, uConfig.cubeNamespace);

    const defaultFilters = viewMode === 'map'
        ? config.explore.defaultFilters.mapViewDefaultFilters
        : config.explore.defaultFilters.listViewDefaultFilters;

    return _.union(
        dynamicCubeFilters,
        cubeUserFilters,
        restrictionFilters,
        textSearchFilters,
        defaultFilters
    );
};

// --- Thunks ---
export const searchUniverse = createAsyncThunk(
    'universeQuery/search',
    async ({ universeKey, page = 1, pageSize }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const exploreByContact = state.universe.universes[universeKey]?.exploreByContact ?? false;

            const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';
            const uConfig = resolveUniverseConfig(universeKey, exploreByContact);

            if (!uConfig) throw new Error(`Unknown Universe or Mode for: ${universeKey}`);

            const limit = pageSize || config.constants.EXPLORE_LIST_VIEW_LIMIT || 20;
            const offset = (page - 1) * limit;

            const filters = prepareFilters(state, universeKey, 'list');

            const payload = {
                dimensions: uConfig.listDimensions,
                timeDimensions: [],
                ungrouped: true,
                filters: filters,
                order: uConfig.defaultOrder,
                limit: limit,
                offset: offset
            };

            console.log(`[search] universeKey [${universeKey}] mode [${modeKey}] page [${page}] query built identified [${payload.filters.length}] filters, executing`);

            const resultSet = await CubeApi.load(config.services.ATLAS.cubeBaseUrl, payload);

            console.log(`[search] universeKey [${universeKey}] query executed`);

            return { universeKey, modeKey, data: resultSet };
        } catch (err) {
            return rejectWithValue({ universeKey, message: err.message });
        }
    }
);

export const countUniverse = createAsyncThunk(
    'universeQuery/count',
    async ({ universeKey, isMapEnabled }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const exploreByContact = state.universe.universes[universeKey]?.exploreByContact ?? false;

            const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';
            const uConfig = resolveUniverseConfig(universeKey, exploreByContact);

            if (!uConfig) throw new Error(`Unknown Universe or Mode for: ${universeKey}`);

            const effectiveMapEnabled = isMapEnabled && uConfig.supportsMap;
            const viewMode = effectiveMapEnabled ? 'map' : 'list';

            console.log(`[count] universeKey [${universeKey}] mode [${modeKey}] view mode [${viewMode}], building query`);

            const filters = prepareFilters(state, universeKey, viewMode);

            const countPayload = {
                measures: uConfig.countMeasures,
                timeDimensions: [],
                filters: filters,
            };

            const countResult = await CubeApi.load(config.services.ATLAS.cubeBaseUrl, countPayload);
            const countCompanies = countResult[0][`${uConfig.cubeNamespace}.count_companies`] || 0;
            const countContacts = countResult[0][`${uConfig.cubeNamespace}.count_contacts`] || 0;

            if (countCompanies === 0) {
                return {
                    universeKey,
                    modeKey,
                    countCompanies,
                    countContacts,
                    mapData: null,
                    productDataType: 'RECORD',
                    cubeQuery: filters,
                    cubeName: uConfig.cubeNamespace,
                };
            }

            let mapData = null;
            let productDataType = 'RECORD';

            if (effectiveMapEnabled) {
                const mapClusterMode = countCompanies > config.constants.EXPLORE_MAP_VIEW_CLUSTER_MODE_RECORD_LIMIT
                    ? 'location'
                    : 'record';

                const mapFilters = _.union(filters, [
                    { member: `${uConfig.cubeNamespace}.${uConfig.mapLatField}`, operator: 'set' },
                    { member: `${uConfig.cubeNamespace}.${uConfig.mapLonField}`, operator: 'set' }
                ]);

                if (mapClusterMode === 'location') {
                    const mapPayload = {
                        measures: [
                            `${uConfig.cubeNamespace}.count_companies`,
                            `${uConfig.cubeNamespace}.count_contacts`
                        ],
                        dimensions: [
                            `${uConfig.cubeNamespace}.company_address_county_name`,
                            `${uConfig.cubeNamespace}.${uConfig.mapLatField}`,
                            `${uConfig.cubeNamespace}.${uConfig.mapLonField}`,
                            `${uConfig.cubeNamespace}.company_address_city`,
                            `${uConfig.cubeNamespace}.company_address_region`
                        ],
                        timeDimensions: [],
                        filters: mapFilters,
                        limit: config.constants.EXPLORE_MAP_VIEW_CLUSTER_MODE_QUERY_LIMIT
                    };

                    const resultSet = await CubeApi.load(config.services.ATLAS.cubeBaseUrl, mapPayload);
                    mapData = buildGeoJson(resultSet, uConfig.cubeNamespace, uConfig.mapLatField, uConfig.mapLonField);
                    productDataType = 'LOC_PLACE';
                } else {
                    const recordPayload = {
                        measures: [`${uConfig.cubeNamespace}.count_contacts`],
                        dimensions: [
                            `${uConfig.cubeNamespace}.company_id`,
                            `${uConfig.cubeNamespace}.company_identity`,
                            `${uConfig.cubeNamespace}.company_name`,
                            `${uConfig.cubeNamespace}.company_score`,
                            `${uConfig.cubeNamespace}.company_address_county_name`,
                            `${uConfig.cubeNamespace}.company_address_latitude`,
                            `${uConfig.cubeNamespace}.company_address_longitude`,
                            `${uConfig.cubeNamespace}.company_address_city`,
                            `${uConfig.cubeNamespace}.company_address_region`
                        ],
                        timeDimensions: [],
                        filters: mapFilters,
                        limit: config.constants.EXPLORE_MAP_VIEW_CLUSTER_MODE_QUERY_LIMIT
                    };

                    const resultSet = await CubeApi.load(config.services.ATLAS.cubeBaseUrl, recordPayload);
                    mapData = buildPointDetailGeoJson(resultSet, uConfig.cubeNamespace);
                    productDataType = 'RECORD';
                }
            }

            return {
                universeKey,
                modeKey,
                countCompanies,
                countContacts,
                mapData,
                productDataType,
                cubeQuery: filters,
                cubeName: uConfig.cubeNamespace,
            };

        } catch (err) {
            return rejectWithValue({ universeKey, message: err.message });
        }
    }
);

// --- Slice ---
const universeQuerySlice = createSlice({
    name: 'universeQuery',
    initialState,
    reducers: {
        clearResults: (state, action) => {
            const universeKey = action.payload;
            if (state.results[universeKey]) {
                state.results[universeKey].COMPANY = { ...initialState.results[universeKey].COMPANY };
                state.results[universeKey].CONTACT = { ...initialState.results[universeKey].CONTACT };
                state.results[universeKey].mapStatus = 'idle';
                state.results[universeKey].mapErrorMessage = null;
            }
        }
    },
    extraReducers: (builder) => {
        // --- Clear data when login is fulfilled ---
        builder.addCase(login.fulfilled, (state) => {
            state.searchLoading = false;
            state.countLoading = false;
            state.error = null;
            // Reset results all Universe to default
            state.results.BUSINESS = _.cloneDeep(initialState.results.BUSINESS);
            state.results.CONTRACTOR = _.cloneDeep(initialState.results.CONTRACTOR);
        });

        // --- Search Universe (List Data) ---
        builder.addCase(searchUniverse.pending, (state) => {
            state.searchLoading = true;
            state.error = null;
        });
        builder.addCase(searchUniverse.fulfilled, (state, action) => {
            state.searchLoading = false;

            const { universeKey, modeKey, data } = action.payload;
            state.results[universeKey][modeKey].listData = data;
            state.results[universeKey].lastUpdated = new Date().toISOString();
        });
        builder.addCase(searchUniverse.rejected, (state, action) => {
            state.searchLoading = false;
            const message = action.payload?.message || 'An error occurred while fetching data. Please contact admin.';
            state.error = message;
        });

        // --- Count Universe (Counts & Map Data) ---
        builder.addCase(countUniverse.pending, (state, action) => {
            state.countLoading = true;
            state.error = null;
            const universeKey = action.meta?.arg?.universeKey;
            if (universeKey && state.results[universeKey]) {
                state.results[universeKey].mapStatus = 'loading';
                state.results[universeKey].mapErrorMessage = null;
            }
        });
        builder.addCase(countUniverse.fulfilled, (state, action) => {
            state.countLoading = false;

            const { universeKey, modeKey, countCompanies, countContacts, mapData, productDataType, cubeQuery, cubeName } = action.payload;

            state.results[universeKey][modeKey].totalCompanies = countCompanies;
            state.results[universeKey][modeKey].totalContacts = countContacts;
            state.results[universeKey][modeKey].mapData = mapData || { features: [] };
            state.results[universeKey][modeKey].appliedFilters = {
                totalCompanies: countCompanies,
                totalContacts: countContacts,
                cubeQuery: cubeQuery,
                cubeName: cubeName,
            };

            state.results[universeKey].productDataType = productDataType;
            state.results[universeKey].lastUpdated = new Date().toISOString();
            state.results[universeKey].mapStatus = 'succeed';
            state.results[universeKey].mapErrorMessage = null;
        });
        builder.addCase(countUniverse.rejected, (state, action) => {
            state.countLoading = false;
            const universeKey = action.payload?.universeKey;
            const message = action.payload?.message || 'An error occurred while fetching data. Please contact admin.';
            if (universeKey && state.results[universeKey]) {
                state.results[universeKey].mapStatus = 'error';
                state.results[universeKey].mapErrorMessage = message;
            }
            state.error = message;
        });
    }
});

export const { clearResults } = universeQuerySlice.actions;
export default universeQuerySlice.reducer;
